import { useRef, useState } from 'react';
import { Download, FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import Modal from './Modal';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';
import {
    createBillingTimeImportRequestId,
    downloadBillingTimeTemplate,
    parseBillingTimeFile,
} from './billingTimeImport';

const apiOrigin = () => {
    const configured = String(process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');
    if (configured) return configured;
    if (typeof window === 'undefined') return '';
    const { protocol, hostname, origin } = window.location;
    if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('api.')) {
        return origin;
    }
    const labels = hostname.split('.').filter(Boolean);
    const applicationRoot = labels.length > 2 ? labels.slice(-2).join('.') : hostname;
    return `${protocol}//api.${applicationRoot}`;
};

const IMPORT_ENDPOINT = `${apiOrigin()}/billing_time_import.php`;

const readImportResponse = async (response) => {
    const text = await response.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch (error) {
        throw new Error('CaseDB returned an invalid response while reviewing the spreadsheet.');
    }
    if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'The time spreadsheet could not be reviewed.');
    }
    return data;
};

const displayTime = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
};

const displayRate = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—';
};

const BillingTimeImportModal = ({ caseId, onClose, onImported }) => {
    const [fileName, setFileName] = useState('');
    const [sheetName, setSheetName] = useState('');
    const [rows, setRows] = useState([]);
    const [preview, setPreview] = useState(null);
    const [requestId, setRequestId] = useState('');
    const [sourceSha256, setSourceSha256] = useState('');
    const [reviewing, setReviewing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState('');
    const requestInFlightRef = useRef(false);

    const postRows = async (action, nextRows, nextRequestId, extra = {}) => {
        await ensureCaseDbCsrfToken();
        const response = await fetch(IMPORT_ENDPOINT, {
            method: 'POST',
            credentials: 'include',
            headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
            body: JSON.stringify({
                action,
                case_id: Number(caseId),
                rows: nextRows,
                request_id: nextRequestId,
                source_file: extra.source_file ?? fileName,
                sheet_name: extra.sheet_name ?? sheetName,
                source_sha256: extra.source_sha256 ?? sourceSha256,
                ...extra,
            }),
        });
        return readImportResponse(response);
    };

    const handleFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || requestInFlightRef.current) return;

        requestInFlightRef.current = true;
        setReviewing(true);
        setError('');
        setPreview(null);
        setRows([]);
        setFileName(file.name);
        setSheetName('');
        setSourceSha256('');
        try {
            const parsed = await parseBillingTimeFile(file);
            const nextRequestId = createBillingTimeImportRequestId();
            setRows(parsed.rows);
            setSheetName(parsed.sheetName);
            setSourceSha256(parsed.sourceSha256 || '');
            setRequestId(nextRequestId);
            const data = await postRows('preview', parsed.rows, nextRequestId, {
                source_file: file.name,
                sheet_name: parsed.sheetName,
                source_sha256: parsed.sourceSha256 || '',
            });
            setPreview(data);
        } catch (uploadError) {
            setError(uploadError?.message || 'The time spreadsheet could not be reviewed.');
        } finally {
            requestInFlightRef.current = false;
            setReviewing(false);
        }
    };

    const importRows = async () => {
        const readyCount = Number(preview?.summary?.ready_rows || 0);
        const errorCount = Number(preview?.summary?.error_rows || 0);
        if (requestInFlightRef.current || readyCount <= 0 || errorCount > 0) return;

        requestInFlightRef.current = true;
        setImporting(true);
        setError('');
        try {
            const data = await postRows('import', rows, requestId, { confirmed: true });
            await onImported?.(data);
        } catch (importError) {
            setError(importError?.message || 'The time entries could not be imported.');
        } finally {
            requestInFlightRef.current = false;
            setImporting(false);
        }
    };

    const summary = preview?.summary || {};
    const readyCount = Number(summary.ready_rows || 0);
    const errorCount = Number(summary.error_rows || 0);
    const skippedCount = Number(summary.skipped_blank_time || 0);
    const increment = Number(summary.increment_hours || 0);

    return (
        <Modal
            title='Import Time'
            instructions='Review spreadsheet entries before adding them to this case.'
            wider
            onClose={reviewing || importing ? null : onClose}
            footer={(
                <div className='modal-footer-actions'>
                    <button className='action alt' onClick={onClose} disabled={reviewing || importing}>Cancel</button>
                    <button
                        className='action'
                        onClick={importRows}
                        disabled={reviewing || importing || !preview || readyCount === 0 || errorCount > 0}
                        aria-busy={importing}
                    >
                        {importing ? <><Loader2 size={16} className='spin' /> Importing…</> : `Import ${readyCount} ${readyCount === 1 ? 'Entry' : 'Entries'}`}
                    </button>
                </div>
            )}
        >
            <div className='modal-content-wrapper billing-time-import-modal'>
                <div className='instructions billing-time-import-guide'>
                    <strong>Spreadsheet format</strong>
                    <span>Use columns named Date, Description, and Time. User is optional and may contain an exact CaseDB name, username, email, or user ID. Import up to 2,000 rows at a time.</span>
                    <span>Blank Time cells are ignored. A blank User cell uses your account. Imported time is rounded up to this case&apos;s minimum billing increment.</span>
                    <span>CaseDB reads the first non-empty worksheet and shows a preview before saving anything.</span>
                </div>

                <div className='billing-time-import-actions'>
                    <label className={`action alt billing-time-import-file ${reviewing || importing ? 'disabled' : ''}`} style={{ fontSize: '13.33px' }}>
                        {reviewing ? <Loader2 size={16} className='spin' /> : <Upload size={16} />}
                        {reviewing ? 'Reviewing…' : fileName ? 'Choose Another File' : 'Choose Excel or CSV'}
                        <input type='file' accept='.csv,.xls,.xlsx,text/csv' onChange={handleFile} disabled={reviewing || importing} />
                    </label>
                    <button className='action alt' type='button' onClick={downloadBillingTimeTemplate} disabled={reviewing || importing}>
                        <Download size={16} /> Download Template
                    </button>
                    {fileName && (
                        <span className='billing-time-import-file-name'>
                            <FileSpreadsheet size={16} /> {fileName}{sheetName ? ` · ${sheetName}` : ''}
                        </span>
                    )}
                </div>

                {error && <div className='billing-time-import-error' role='alert'>{error}</div>}

                {preview && (
                    <>
                        <div className='billing-time-import-summary'>
                            <span className='tag'>{readyCount} ready</span>
                            {skippedCount > 0 && <span className='tag'>{skippedCount} blank-time {skippedCount === 1 ? 'row' : 'rows'} ignored</span>}
                            {errorCount > 0 && <span className='tag error'>{errorCount} {errorCount === 1 ? 'row needs' : 'rows need'} attention</span>}
                            <span className='small-text'>Billing increment: {increment > 0 ? `${increment} hour${increment === 1 ? '' : 's'}` : 'None'}</span>
                            {summary.schedule_name && <span className='small-text'>Rate schedule: {summary.schedule_name}</span>}
                        </div>

                        {errorCount > 0 && (
                            <div className='billing-time-import-error'>Fix every highlighted row in the spreadsheet, then upload it again. Nothing has been saved.</div>
                        )}

                        <div className='billing-time-import-preview'>
                            <table className='billing-time-import-table'>
                                <colgroup>
                                    <col className='billing-time-import-col-row' />
                                    <col className='billing-time-import-col-date' />
                                    <col className='billing-time-import-col-description' />
                                    <col className='billing-time-import-col-user' />
                                    <col className='billing-time-import-col-time' />
                                    <col className='billing-time-import-col-time' />
                                    <col className='billing-time-import-col-rate' />
                                    <col className='billing-time-import-col-status' />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Row</th>
                                        <th>Date</th>
                                        <th>Description</th>
                                        <th>Team Member</th>
                                        <th>Original</th>
                                        <th>Import</th>
                                        <th>Rate</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.rows || []).map((row) => (
                                        <tr key={`${row.source_row}-${row.user_id || 'unresolved'}`} className={row.status === 'error' ? 'billing-time-import-row-error' : ''}>
                                            <td>{row.source_row}</td>
                                            <td>{row.date || '—'}</td>
                                            <td className='align-start'>{row.description || '—'}</td>
                                            <td>{row.user_name || '—'}</td>
                                            <td>{displayTime(row.original_time)}</td>
                                            <td>{displayTime(row.rounded_time)}</td>
                                            <td>{displayRate(row.rate)}</td>
                                            <td>{row.status === 'error' ? <span className='billing-time-import-row-message'>{row.error}</span> : <span className='tag'>Ready</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default BillingTimeImportModal;
